import { API_BASE } from '../apiBase';
// src/projects/ProjectProfile.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import ProjectFinances from './ProjectFinances';
import ProjectProfile_DocsMedia from './ProjectProfile_Docs&Media';
import EmployeeReceipts from './EmployeeReceipts';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  ChevronLeft, ChevronRight, LayoutDashboard, Calendar, Users, Wallet,
  FileText, Info, MapPin, Tag, Plus, X, Check, Bell, BellOff, Eye, EyeOff
} from 'lucide-react';
import JobsiteMiniMap from '../lib/JobsiteMiniMap';

// ---- "Work for" picker (project details) — who SENT us this job. Searches
// the Relationships saved under Contacts; clearing it makes the project ours.
function WorkForPicker({ value, onPick, onClear }) {
  const [q, setQ] = useState('');
  const [rels, setRels] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/relationships`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => setRels((r.data || []).map(x => ({ id: x._id, company: x.company, phone: x.phone || '' }))))
      .catch(() => setRels([]));
  }, []);
  const hits = q.trim()
    ? rels.filter(r => r.company.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 5)
    : [];
  return (
    <div>
      {value?.name ? (
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-semibold">{value.name}</span>
          {value.phone && <span className="text-zinc-400 text-xs">{value.phone}</span>}
          <button onClick={onClear} className="text-xs text-red-400 hover:text-red-300 underline underline-offset-2">remove</button>
        </div>
      ) : (
        <div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your Relationships (e.g. James Electric)…"
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-sm text-white outline-none focus:border-indigo-500"
          />
          {hits.length > 0 && (
            <div className="mt-1 border border-gray-700 rounded-xl overflow-hidden bg-gray-800">
              {hits.map(r => (
                <button
                  key={r.id}
                  onClick={() => { onPick(r); setQ(''); }}
                  className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0 flex justify-between gap-2"
                >
                  <span className="truncate">{r.company}</span>
                  {r.phone && <span className="text-xs text-zinc-500 shrink-0">{r.phone}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || 'employee';
  const location = useLocation();
  const [relatedProjects, setRelatedProjects] = useState([]);
  // Save the current project URL so SideNav can bring us back here
  useEffect(() => {
    localStorage.setItem('lastProjectUrl', location.pathname);
  }, [location.pathname]);
  const isOwner = role === 'superadmin';

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]); // eligible PMs for the Team tab
  const [employeeUsers, setEmployeeUsers] = useState([]); // employee-role logins for Assigned Employees
  const [pmSaving, setPmSaving] = useState(false);

  // Record this project as recently-viewed by the current user (powers the
  // Projects table "Recently viewed" sort, synced server-side).
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API_BASE}/projects/${id}/viewed`, {}, { headers: { Authorization: token } }).catch(() => {});
    }
  }, [id]);

  // Load eligible users once (for reassigning the Project Manager).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_BASE}/users`, { headers: { Authorization: token } })
      .then(res => {
        setTeamUsers((res.data || []).filter(u => ['superadmin', 'admin', 'projectManager'].includes(u.role)));
        setEmployeeUsers((res.data || []).filter(u => u.role === 'employee' && !u.isBlocked));
      })
      .catch(() => {});
  }, []);

  const handleSavePM = async (userId) => {
    if (!userId) return;
    setPmSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, { projectManager: userId }, { headers: { Authorization: token } });
      const u = teamUsers.find(x => x._id === userId);
      setProject(prev => ({ ...prev, projectManager: u || prev.projectManager }));
    } catch (err) {
      alert('Failed to update Project Manager.');
    } finally {
      setPmSaving(false);
    }
  };

  // ---- Additional (co-)project managers ----
  const [addingCoPM, setAddingCoPM] = useState(false);
  const [coPmSaving, setCoPmSaving] = useState(false);
  const currentUserId = localStorage.getItem('userId') || '';
  const primaryPmId = project?.projectManager?._id || '';
  // Super Admin manages everything; the primary PM manages their own co-managers
  // (add, remove, and the per-person notification toggle).
  const canManageCoPMs = isOwner || (!!currentUserId && currentUserId === primaryPmId);

  const saveAdditionalManagers = async (next) => {
    setCoPmSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = next.map(m => ({ user: m.user?._id || m.user, receiveNotifications: !!m.receiveNotifications }));
      const res = await axios.put(`${API_BASE}/projects/${id}`, { additionalManagers: payload }, { headers: { Authorization: token } });
      setProject(prev => ({ ...prev, additionalManagers: res.data?.additionalManagers || next }));
      fetchTeamHistory(true); // new entries land at the top
    } catch (err) {
      alert('Failed to update co-managers.');
    } finally {
      setCoPmSaving(false);
    }
  };

  const handleAddCoPM = (userId) => {
    if (!userId) return;
    setAddingCoPM(false);
    const u = teamUsers.find(x => x._id === userId);
    if (!u) return;
    saveAdditionalManagers([...(project?.additionalManagers || []), { user: u, receiveNotifications: false }]);
  };

  const handleRemoveCoPM = (userId) => {
    saveAdditionalManagers((project?.additionalManagers || []).filter(m => (m.user?._id || m.user) !== userId));
  };

  const handleToggleCoPmNotif = (userId) => {
    saveAdditionalManagers((project?.additionalManagers || []).map(m =>
      (m.user?._id || m.user) === userId ? { ...m, receiveNotifications: !m.receiveNotifications } : m
    ));
  };
  // ---- Assigned Employees (crew) — eyeball = can view; three tab toggles ----
  const [addingEmp, setAddingEmp] = useState(false);
  const [empSaving, setEmpSaving] = useState(false);
  const saveAssignedEmployees = async (next) => {
    setEmpSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = next.map(a => ({
        user: a.user?._id || a.user,
        canView: !!a.canView, timeline: !!a.timeline, docsMedia: !!a.docsMedia, receipts: !!a.receipts,
        docsView: a.docsView || 'limited', videosView: a.videosView || 'limited', galleryView: a.galleryView || 'limited',
      }));
      await axios.put(`${API_BASE}/projects/${id}`, { assignedEmployees: payload }, { headers: { Authorization: token } });
      // Keep OUR copy (it has populated user objects AND every field) — echoing
      // the server response back can silently revert fields an out-of-date
      // backend dropped, which looks like a "blink".
      setProject(prev => ({ ...prev, assignedEmployees: next }));
      fetchTeamHistory(true); // new entries land at the top
    } catch (err) {
      alert('Failed to update assigned employees.');
    } finally {
      setEmpSaving(false);
    }
  };
  const handleAddEmployee = (userId) => {
    if (!userId) return;
    setAddingEmp(false);
    const u = employeeUsers.find(x => x._id === userId);
    if (!u) return;
    saveAssignedEmployees([...(project?.assignedEmployees || []), { user: u, canView: false, timeline: false, docsMedia: false, receipts: false }]);
  };
  const handleRemoveEmployee = (userId) => {
    saveAssignedEmployees((project?.assignedEmployees || []).filter(a => (a.user?._id || a.user) !== userId));
  };
  const handleToggleEmpFlag = (userId, flag) => {
    saveAssignedEmployees((project?.assignedEmployees || []).map(a =>
      (a.user?._id || a.user) === userId ? { ...a, [flag]: !a[flag] } : a
    ));
  };
  // Per-category media permission (all | limited | none).
  const handleSetEmpPerm = (userId, field, value) => {
    saveAssignedEmployees((project?.assignedEmployees || []).map(a =>
      (a.user?._id || a.user) === userId ? { ...a, [field]: value } : a
    ));
  };

  // Super Admin right-click on a Team History entry → delete it (testing noise).
  const [histCtx, setHistCtx] = useState(null); // { entryId, x, y }
  const histContext = (entryId) => (e) => {
    if (!isOwner) return; // superadmin only
    e.preventDefault();
    setHistCtx({ entryId, x: e.clientX, y: e.clientY });
  };
  const deleteHistEntry = async () => {
    const { entryId } = histCtx;
    setHistCtx(null);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/projects/${id}/team-history/${entryId}`, { headers: { Authorization: token } });
      setTeamHist(prev => ({ entries: prev.entries.filter(h => h._id !== entryId), total: Math.max(prev.total - 1, 0) }));
    } catch (err) {
      alert('Could not delete the history entry.');
    }
  };

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem('projectActiveTab') || 'overview'
  );

  // ---- Team History: its own paginated fetch (10 first, +20 per scroll) ----
  const [teamHist, setTeamHist] = useState({ entries: [], total: 0 });
  const [histLoading, setHistLoading] = useState(false);
  const fetchTeamHistory = async (reset = false) => {
    if (role === 'employee') return;
    const skip = reset ? 0 : teamHist.entries.length;
    const limit = reset ? 10 : 20;
    setHistLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/projects/${id}/team-history?skip=${skip}&limit=${limit}`, { headers: { Authorization: token } });
      setTeamHist(prev => {
        const base = reset ? [] : prev.entries;
        const seen = new Set(base.map(e => e._id));
        return { entries: [...base, ...(r.data.entries || []).filter(e => !seen.has(e._id))], total: r.data.total || 0 };
      });
    } catch (_) { /* card just stays as-is */ }
    finally { setHistLoading(false); }
  };
  useEffect(() => { if (activeTab === 'team' && id) fetchTeamHistory(true); }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const onHistScroll = (e) => {
    const el = e.currentTarget;
    if (histLoading || teamHist.entries.length >= teamHist.total) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) fetchTeamHistory(false);
  };


  // Cross-link from the Expense Ledger: when we arrived by clicking a PO there,
  // open straight to Finances, highlight that expense, and make Back return to
  // the Ledger. This only holds while we're on the Finances tab — switching to
  // any other tab resets Back to its normal "All Projects" behavior.
  const [cameFromLedger, setCameFromLedger] = useState(false);
  // Cross-link from another project's Documents card (clicking a global file's
  // globe): Back shows "< PO#xxxx" and returns to the originating project.
  const [cameFromProject, setCameFromProject] = useState(null); // { id, po }
  const [ledgerHighlightId, setLedgerHighlightId] = useState(null);
  const [highlightExpenseId, setHighlightExpenseId] = useState(null);
  const [openFinancesExpenses, setOpenFinancesExpenses] = useState(false);

  useEffect(() => {
    if (location.state?.fromLedger) {
      setCameFromLedger(true);
      setLedgerHighlightId(location.state.ledgerHighlightId || null);
      setHighlightExpenseId(location.state.highlightExpenseId || null);
      setActiveTab('finances');
      localStorage.setItem('projectActiveTab', 'finances');
    }
    // Jump from the Pending-Review oversight: open Finances → Expenses queue,
    // and let Back return to the Ledger.
    if (location.state?.goToReview) {
      setCameFromLedger(true);
      setOpenFinancesExpenses(true);
      setActiveTab('finances');
      localStorage.setItem('projectActiveTab', 'finances');
    }
    // Arrived via a global file's globe icon: open straight to Documents and
    // remember where we came from so Back reads "< PO#xxxx".
    if (location.state?.goToDocs) {
      setActiveTab('documents');
      localStorage.setItem('projectActiveTab', 'documents');
      setCameFromProject(location.state.fromProject || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

    const handleTabChange = (tab) => {
      setActiveTab(tab);
      localStorage.setItem('projectActiveTab', tab);
      // Leaving Finances drops the ledger link (Back reverts to All Projects).
      if (tab !== 'finances') {
        setCameFromLedger(false);
        setHighlightExpenseId(null);
      }
      // Leaving Documents drops the origin-project link the same way.
      if (tab !== 'documents') setCameFromProject(null);
    };
  // Documents & Media
  const [files, setFiles] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAllAddressMedia, setShowAllAddressMedia] = useState(false);

  // Overview states
  const [editingSection, setEditingSection] = useState(null);
  const [tempProject, setTempProject] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [showCopied, setShowCopied] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState("");

  // Notes
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteParentId, setNewNoteParentId] = useState(null);

  // Scope
  const [showScopeEditor, setShowScopeEditor] = useState(false);
  const [scopeContent, setScopeContent] = useState("");

  // Read tab from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['overview', 'timeline', 'team', 'documents', 'finances'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

             useEffect(() => {
    const fetchProject = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("No authentication token found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${API_BASE}/projects/${id}`, { 
          headers: { Authorization: token } 
        });

        // SAFETY NET: If the database returns nothing, stop the code and show an error
        if (!res.data) {
          setError("Project not found. It may have been deleted.");
          setLoading(false);
          return;
        }

        console.log("✅ Project loaded:", res.data.title);

        setProject(res.data);
        setTempTitle(res.data?.title || '');
        setNotes(res.data?.notes || []);
        setScopeContent(res.data?.description || "");
        // Fetch related projects (same address)
        try {
          const relatedRes = await axios.get(`${API_BASE}/projects/${id}/related`, {
            headers: { Authorization: token }
          });
          setRelatedProjects(relatedRes.data);
        } catch (relatedErr) {
          console.error("Failed to fetch related projects", relatedErr);
        }

      } catch (err) {
        console.error("Project fetch error:", err.response?.data || err.message);
        setError("Failed to load project. Please try logging out and back in.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
    
  }, [id]);

    // Simple fetchFiles
  const fetchFiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token'); // Retrieve the token
      if (!token) {
        console.error("No token found in localStorage");
        return;
      }

      const res = await axios.get(`${API_BASE}/projects/${id}/files`, {
        headers: { Authorization: `Bearer ${token}` } // Send it as a Bearer token
      });

      // Newest first. Dropbox returns files oldest→newest; sort by upload time
      // (server_modified, then client_modified) descending, tie-break on name.
      const sorted = [...(res.data || [])].sort((a, b) => {
        const ta = new Date(a.server_modified || a.client_modified || 0).getTime();
        const tb = new Date(b.server_modified || b.client_modified || 0).getTime();
        if (tb !== ta) return tb - ta;
        return (b.name || '').localeCompare(a.name || '');
      });
      setFiles(sorted);
      setSelectedIndices([]);
    } catch (err) {
      console.error("Files fetch error:", err.response?.data || err.message);
    }
  }, [id]);

    // Load files when Documents tab is active
  useEffect(() => {
    if (activeTab === 'documents' && id) {
      fetchFiles();
    }
  }, [activeTab, id, fetchFiles]);

  // ==================== NOTES ====================
  const addNote = async () => {
    if (!newNoteText.trim() || !project) return;

    const newNote = {
      id: Date.now().toString(),
      text: newNoteText.trim(),
      author: localStorage.getItem('name') || 'You',
      createdAt: new Date().toISOString(),
      parentId: newNoteParentId || null,
      children: []
    };

    let updatedNotes = [...notes];

    const addToParent = (noteList) => {
      for (let note of noteList) {
        if (note.id === newNoteParentId) {
          note.children = note.children || [];
          note.children.push(newNote);
          return true;
        }
        if (addToParent(note.children || [])) return true;
      }
      return false;
    };

    if (newNoteParentId) {
      addToParent(updatedNotes);
    } else {
      updatedNotes.push(newNote);
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, { notes: updatedNotes }, { 
        headers: { Authorization: token } 
      });

      setNotes(updatedNotes);
      setProject(prev => ({ ...prev, notes: updatedNotes }));
      setNewNoteText("");
      setNewNoteParentId(null);
    } catch (err) {
      console.error("Failed to save note", err);
      alert("Failed to save note");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note and all its children?")) return;

    const removeNoteRecursive = (noteList) => {
      return noteList.filter(note => {
        if (note.id === noteId) return false;
        note.children = removeNoteRecursive(note.children || []);
        return true;
      });
    };

    const updatedNotes = removeNoteRecursive([...notes]);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, { notes: updatedNotes }, { 
        headers: { Authorization: token } 
      });
      setNotes(updatedNotes);
      setProject(prev => ({ ...prev, notes: updatedNotes }));
    } catch (err) {
      console.error("Failed to delete note", err);
      alert("Failed to delete note");
    }
  };

  const renderNotes = (noteList, level = 0) => {
    return noteList.map((note) => (
      <div key={note.id} style={{ paddingLeft: `${level * 24}px` }} className="border-l-2 border-indigo-500/30 mb-5">
        <div className="flex justify-between items-start group">
          <div>
            <p className="text-zinc-200 font-medium cursor-pointer hover:text-white transition-colors" 
               onClick={() => copyToClipboard(note.text)}>
              {note.text}
            </p>
            <p className="text-[10px] text-zinc-500 mt-1">
              {new Date(note.createdAt).toLocaleDateString()} • {note.author || 'You'}
            </p>
          </div>
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => setNewNoteParentId(note.id)} className="text-indigo-400 hover:text-indigo-300 text-xs px-2 py-1">+ Sub-note</button>
            <button onClick={() => deleteNote(note.id)} className="text-red-400 hover:text-red-300 text-xs px-2 py-1">Delete</button>
          </div>
        </div>
        {note.children && note.children.length > 0 && (
          <div className="mt-3">
            {renderNotes(note.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };


  // ==================== OTHER HELPERS ====================
  const handleToggleSelect = (index) => {
    if (lastSelectedIndex === null || selectedIndices.length === 0) {
      setSelectedIndices([index]);
      setLastSelectedIndex(index);
    } else {
      const start = Math.min(index, lastSelectedIndex);
      const end = Math.max(index, lastSelectedIndex);
      setSelectedIndices(Array.from({ length: end - start + 1 }, (_, i) => start + i));
      setLastSelectedIndex(index);
    }
  };

  const deleteFile = async (path) => {
    if (!window.confirm('Delete this file?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_BASE}/projects/${id}/files`, { data: { path }, headers: { Authorization: token } });
      fetchFiles();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const addTag = async (e) => {
    if (e.key === 'Enter' && newTag.trim() !== "" && project) {
      e.preventDefault();
      const updatedTags = [...(project.tags || []), newTag.trim()];
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_BASE}/projects/${id}`, { tags: updatedTags }, { headers: { Authorization: token } });
        setProject({ ...project, tags: updatedTags });
        setNewTag("");
      } catch (err) { console.error(err); }
    }
  };

  const removeTag = async (tagToRemove) => {
    if (!project) return;
    const updatedTags = project.tags.filter(t => t !== tagToRemove);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, { tags: updatedTags }, { headers: { Authorization: token } });
      setProject({ ...project, tags: updatedTags });
    } catch (err) { console.error(err); }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1500);
  };

  const handleRenameTitle = async () => {
    if (tempTitle.trim() === "" || tempTitle === project.title) return setIsRenaming(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_BASE}/projects/${id}`, { title: tempTitle.trim() }, {
        headers: { Authorization: token }
      });
      setProject({ ...project, title: res.data.title });
      setIsRenaming(false);
    } catch (err) {
      alert("Failed to rename project");
      setTempTitle(project.title);
      setIsRenaming(false);
    }
  };

  const saveSection = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, tempProject, { headers: { Authorization: token } });
      setProject(tempProject);
      setEditingSection(null);
    } catch (err) { 
      alert('Save failed'); 
    }
  };

  const saveScope = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${id}`, { description: scopeContent }, { headers: { Authorization: token } });
      setProject(prev => ({ ...prev, description: scopeContent }));
      setShowScopeEditor(false);
    } catch (err) {
      alert('Failed to save scope');
    }
  };

  const handleCustomerInteraction = () => {
    if (project?.customer?.name) copyToClipboard(project.customer.name);
  };

  const handleCustomerNavigate = () => {
    if (project?.customer?._id) navigate(`/customers/${project.customer._id}`);
  };

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center font-bold text-zinc-500 uppercase tracking-widest">Loading Project...</div>;
  if (error || !project) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-red-400 font-bold">{error || "Project not found"}</div>;

  // Employees see only Overview + whatever their assignment toggles allow.
  const isEmployeeRole = role === 'employee';
  const myAssignment = isEmployeeRole
    ? (project.assignedEmployees || []).find(a => (a.user?._id || a.user) === (localStorage.getItem('userId') || '')) || {}
    : null;
  const tabs = isEmployeeRole
    ? [
        { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
        ...(myAssignment.timeline ? [{ id: 'timeline', label: 'Timeline', icon: <Calendar size={18} /> }] : []),
        ...(myAssignment.docsMedia ? [{ id: 'documents', label: 'Documents & Media', icon: <FileText size={18} /> }] : []),
        ...(myAssignment.receipts ? [{ id: 'receipts', label: 'My Receipts', icon: <Wallet size={18} /> }] : []),
      ]
    : [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
    { id: 'timeline', label: 'Timeline', icon: <Calendar size={18} /> },
    { id: 'team', label: 'Team', icon: <Users size={18} /> },
    { id: 'documents', label: 'Documents & Media', icon: <FileText size={18} /> },
    ...(isOwner ? [{ id: 'finances', label: 'Finances', icon: <Wallet size={18} /> }] : []),
  ];
  // If a remembered tab isn't allowed for this user, fall back to Overview.
  const allowedTabIds = tabs.map(t => t.id);
  const safeActiveTab = allowedTabIds.includes(activeTab) ? activeTab : 'overview';

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4">

      {/* HEADER */}
      <div className="max-w-7xl mx-auto mb-10 max-md:mb-6">
        <div className="flex justify-between items-end mb-8 max-md:mb-5 max-md:flex-col-reverse max-md:items-start max-md:gap-3">
          <div>
            {isRenaming ? (
              <input
                autoFocus
                className="text-3xl font-bold tracking-tight bg-gray-900 border-b border-indigo-500 outline-none text-white pb-1"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onBlur={handleRenameTitle}
                onKeyDown={(e) => e.key === 'Enter' && handleRenameTitle()}
              />
            ) : (
              <h1 onDoubleClick={() => setIsRenaming(true)} className="text-3xl max-md:text-xl font-bold tracking-tight cursor-text select-none">
                {project.title}
              </h1>
            )}
            <div className="flex items-center gap-4 mt-2">
              <span className="text-zinc-500 font-mono text-lg">{project.poNumber}</span>
              <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-xs font-black uppercase">{project.status}</span>
            </div>
          </div>
          {(() => {
            const backToLedger = cameFromLedger && safeActiveTab === 'finances';
            const backToProject = cameFromProject && safeActiveTab === 'documents';
            return (
              <button
                onClick={() => {
                  if (backToLedger) return navigate('/finances/ExpenseLedger', { state: { highlightId: ledgerHighlightId } });
                  if (backToProject) return navigate(`/projects/${cameFromProject.id}`, { state: { goToDocs: true, restoreGlobalDocs: !!cameFromProject.docsGlobal } });
                  navigate('/projects');
                }}
                className="text-zinc-500 hover:text-white uppercase text-xs font-black flex items-center gap-2 transition-colors"
              >
                <ChevronLeft size={16}/> {backToLedger ? 'Expense Ledger' : backToProject ? `PO#${cameFromProject.po}` : 'All Projects'}
              </button>
            );
          })()}
        </div>

        {/* Tabs scroll horizontally on phones instead of wrapping/cramping */}
        <div className="flex gap-8 max-md:gap-5 border-b border-gray-800 max-md:overflow-x-auto max-md:[-webkit-overflow-scrolling:touch] max-md:[scrollbar-width:none]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 pb-4 max-md:pb-3 px-1 text-xs font-black uppercase tracking-widest transition-all max-md:shrink-0 max-md:whitespace-nowrap ${
                safeActiveTab === tab.id ? 'border-b-2 border-indigo-500 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT — on phones, long values (emails, addresses, file names)
          wrap instead of stretching cards past the screen edge */}
      <div className="max-w-7xl mx-auto max-md:[overflow-wrap:anywhere] max-md:min-w-0">
        {safeActiveTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Project Details Card */}
              <div className="bg-gray-900 border border-gray-800/50 p-8 max-md:p-5 rounded-3xl shadow-2xl relative">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="text-xl font-bold flex items-center gap-3"><Info size={22} className="text-indigo-500" /> Project Details</h3>
                  <button onClick={() => editingSection === 'details' ? saveSection() : (setEditingSection('details'), setTempProject(project))} className="bg-gray-800 p-2 rounded-xl text-zinc-400">
                    {editingSection === 'details' ? <Check className="text-emerald-500" /> : <Plus />}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-8">
                    
                    {/* NEW: Editable Project Title */}
                    {editingSection === 'details' && (
                      <div>
                        <p className="text-indigo-400 text-[10px] font-black uppercase mb-2">Project Title</p>
                        <input
                          value={tempProject?.title || ''}
                          onChange={(e) => setTempProject({...tempProject, title: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none focus:border-indigo-500 transition-colors"
                          placeholder="Enter Project Title"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 max-md:grid-cols-1 gap-8 max-md:gap-5">
                      <div>
                        <p className="text-indigo-400 text-[10px] font-black uppercase mb-2">Customer Name</p>
                        <p className="text-xl font-bold text-white cursor-pointer select-none" onClick={handleCustomerInteraction} onDoubleClick={handleCustomerNavigate}>
                          {project.customer?.name || 'No Customer Assigned'}
                        </p>
                      </div>
                      <div>
                        <p className="text-indigo-400 text-[10px] font-black uppercase mb-2">Customer Email</p>
                        <p className="text-zinc-300 cursor-pointer hover:text-white transition-colors" onClick={() => copyToClipboard(project.customer?.primaryEmail || project.customer?.email || '')}>
                          {project.customer?.primaryEmail || project.customer?.email || 'No email on file'}
                        </p>
                      </div>
                      <div>
                        <p className="text-indigo-400 text-[10px] font-black uppercase mb-2">Customer Phone</p>
                        <p className="text-zinc-300 cursor-pointer hover:text-white transition-colors" onClick={() => copyToClipboard(project.customer?.primaryPhone || project.customer?.phone || '')}>
                          {project.customer?.primaryPhone || project.customer?.phone || 'No phone on file'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-[10px] font-black uppercase mb-3 flex items-center gap-2"><MapPin size={14} className="text-indigo-500" /> Job Site Address</p>
                      {editingSection === 'details' ? (
                        <div className="space-y-3">
                          <input placeholder="Street" value={tempProject?.jobSiteAddress?.street || ''} onChange={(e) => setTempProject({...tempProject, jobSiteAddress: {...(tempProject.jobSiteAddress || {}), street: e.target.value}})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none" />
                          <div className="grid grid-cols-3 gap-3">
                            <input placeholder="City" value={tempProject?.jobSiteAddress?.city || ''} onChange={(e) => setTempProject({...tempProject, jobSiteAddress: {...(tempProject.jobSiteAddress || {}), city: e.target.value}})} className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none" />
                            <input placeholder="State" value={tempProject?.jobSiteAddress?.state || ''} onChange={(e) => setTempProject({...tempProject, jobSiteAddress: {...(tempProject.jobSiteAddress || {}), state: e.target.value}})} className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none" />
                            <input placeholder="ZIP" value={tempProject?.jobSiteAddress?.zip || ''} onChange={(e) => setTempProject({...tempProject, jobSiteAddress: {...(tempProject.jobSiteAddress || {}), zip: e.target.value}})} className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none" />
                          </div>
                        </div>
                      ) : (
                        <p className="text-lg text-zinc-300 italic cursor-pointer hover:text-white transition-colors" onClick={() => copyToClipboard(project.jobSiteAddress?.street ? `${project.jobSiteAddress.street}, ${project.jobSiteAddress.city} ${project.jobSiteAddress.state}` : 'No address.')}>
                          {project.jobSiteAddress?.street ? `${project.jobSiteAddress.street}, ${project.jobSiteAddress.city} ${project.jobSiteAddress.state}` : 'No address.'}
                        </p>
                      )}
                    </div>

                    {/* Work for — the company that SENT us this job (optional).
                        Empty = our own job, which is most projects. */}
                    {(editingSection === 'details' || project.workForName) && (
                      <div>
                        <p className="text-zinc-500 text-[10px] font-black uppercase mb-3 flex items-center gap-2"><Users size={14} className="text-indigo-500" /> Work For <span className="text-zinc-600 normal-case font-medium">(who sent this job)</span></p>
                        {editingSection === 'details' ? (
                          <WorkForPicker
                            value={{ name: tempProject?.workForName, phone: tempProject?.workForPhone }}
                            onPick={(r) => setTempProject({ ...tempProject, workForRel: r.id, workForName: r.company, workForPhone: r.phone })}
                            onClear={() => setTempProject({ ...tempProject, workForRel: null, workForName: '', workForPhone: '' })}
                          />
                        ) : (
                          <p className="text-lg text-zinc-300">
                            {project.workForName}
                            {project.workForPhone && <span className="text-zinc-500 text-sm"> · {project.workForPhone}</span>}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Bill To — only when this project's customer is tied to a business.
                        Controls whether Quote/Invoice/Receipt show the business name +
                        "Attn:" or just the individual. */}
                    {project.customer?.businessName && (
                      <div>
                        <p className="text-zinc-500 text-[10px] font-black uppercase mb-3 flex items-center gap-2"><Info size={14} className="text-indigo-500" /> Bill To <span className="text-zinc-600 normal-case font-medium">({project.customer.businessName})</span></p>
                        {editingSection === 'details' ? (
                          <select
                            value={tempProject?.billTo || 'Business'}
                            onChange={(e) => setTempProject({ ...tempProject, billTo: e.target.value })}
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-white outline-none focus:border-indigo-500 transition-colors"
                          >
                            <option value="Business">Business — {project.customer.businessName} (Attn: {project.customer.name})</option>
                            <option value="Individual">Individual — {project.customer.name} only</option>
                          </select>
                        ) : (
                          <p className="text-lg text-zinc-300">
                            {project.billTo === 'Individual'
                              ? `Individual — ${project.customer.name}`
                              : `Business — ${project.customer.businessName} (Attn: ${project.customer.name})`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Jobsite map — full card width, dot centered on the address */}
                {editingSection !== 'details' && project.jobSiteAddress?.street && (
                  <JobsiteMiniMap className="mt-8" address={[project.jobSiteAddress.street, project.jobSiteAddress.city, project.jobSiteAddress.state, project.jobSiteAddress.zip].filter(Boolean).join(', ')} />
                )}
              </div>

              {/* Project Scope */}
              <div className="bg-gray-900 border border-gray-800/50 p-8 max-md:p-5 rounded-3xl shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold flex items-center gap-3"><FileText className="text-indigo-500" /> Project Scope</h3>
                  <button onClick={() => setShowScopeEditor(!showScopeEditor)} className="bg-gray-800 p-2 rounded-xl text-zinc-400 hover:text-white transition-colors">
                    <Plus size={20} />
                  </button>
                </div>
                {showScopeEditor ? (
                  <div className="space-y-4">
                    <ReactQuill theme="snow" value={scopeContent} onChange={setScopeContent} className="bg-gray-950 border border-gray-700 rounded-2xl" />
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setShowScopeEditor(false)} className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl">Cancel</button>
                      <button onClick={saveScope} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl">Save Scope</button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="prose prose-invert max-w-none text-zinc-300 leading-relaxed cursor-pointer hover:text-white transition-colors min-h-[200px]"
                    onClick={() => copyToClipboard(scopeContent || project.description || '')}
                    dangerouslySetInnerHTML={{ __html: scopeContent || project.description || '<p>No project scope defined yet.</p>' }}
                  />
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Quick Stats + Notes */}
            <div className="space-y-6">
              <div className="bg-gray-900 border border-gray-800/50 p-8 max-md:p-5 rounded-3xl shadow-xl">
                <h3 className="text-sm font-black uppercase tracking-widest text-zinc-500 mb-6">Quick Stats</h3>
                <div className="flex justify-between items-center p-4 bg-gray-800/40 rounded-2xl border border-gray-700/30">
                  <span className="text-zinc-500 text-[10px] font-black uppercase">Status</span>
                  <span className="text-emerald-400 font-black text-xs uppercase">{project.status}</span>
                </div>
                {relatedProjects.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-800">
                    <p className="text-zinc-500 text-[10px] font-black uppercase mb-4 flex items-center gap-2">
                      <MapPin size={14} className="text-indigo-500" /> Related Projects
                    </p>
                    <ul className="space-y-3">
                      {relatedProjects.map(rp => (
                        <li key={rp._id}>
                          <button
                            onClick={() => {
                              navigate(`/projects/${rp._id}`);
                              window.scrollTo(0, 0); 
                            }}
                            className="text-left w-full group block p-3 bg-gray-950/50 rounded-xl border border-gray-800/50 hover:border-indigo-500/50 transition-colors"
                          >
                            <p className="text-sm font-medium text-zinc-300 group-hover:text-white transition-colors truncate">
                              {rp.title}
                            </p>
                            <p className="text-[10px] font-mono text-zinc-500 mt-1 flex justify-between">
                              <span>PO: {rp.poNumber}</span>
                              <span className="uppercase text-indigo-400">{rp.status}</span>
                            </p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-8 pt-8 border-t border-gray-800">
                  <p className="text-zinc-500 text-[10px] font-black uppercase mb-4 flex items-center gap-2"><Tag size={14} className="text-indigo-500" /> Tags</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags?.map((t, i) => (
                      <span key={i} className="px-3 py-1 bg-zinc-800 text-zinc-300 text-[9px] font-black rounded-full uppercase flex items-center gap-2">
                        {t} <X size={10} className="cursor-pointer hover:text-red-500" onClick={() => removeTag(t)} />
                      </span>
                    ))}
                  </div>
                  <input 
                    type="text" 
                    value={newTag} 
                    onKeyDown={addTag} 
                    onChange={(e) => setNewTag(e.target.value)} 
                    placeholder="Add tag..." 
                    className="w-full bg-gray-950 border border-gray-800 rounded-xl px-4 py-2 text-xs text-white outline-none" 
                  />
                </div>

                <div className="mt-8 pt-8 border-t border-gray-800">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-zinc-500 text-[10px] font-black uppercase flex items-center gap-2"><Info size={14} className="text-indigo-500" /> Notes</p>
                    <button onClick={() => setNewNoteParentId(null)} className="text-indigo-400 hover:text-indigo-300">
                      <Plus size={16} />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {notes.length === 0 ? (
                      <p className="text-zinc-500 italic">No notes yet.</p>
                    ) : (
                      renderNotes(notes)
                    )}
                  </div>

                  <div className="mt-6 flex gap-2">
                    <input
                      type="text"
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addNote()}
                      placeholder="Quick note..."
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-sm outline-none"
                    />
                    <button onClick={addNote} className="px-4 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
{safeActiveTab === 'documents' && (
          <ProjectProfile_DocsMedia
            id={id}
            empPerms={isEmployeeRole ? {
              documents: myAssignment?.docsView || 'limited',
              videos: myAssignment?.videosView || 'limited',
              images: myAssignment?.galleryView || 'limited',
            } : null}
            files={files || []}
            setFiles={setFiles}
            selectedIndices={selectedIndices}
            setSelectedIndices={setSelectedIndices}
            lastSelectedIndex={lastSelectedIndex}
            setLastSelectedIndex={setLastSelectedIndex}
            fetchFiles={fetchFiles}
            uploading={uploading}
            uploadProgress={uploadProgress}
            setUploading={setUploading}
            setUploadProgress={setUploadProgress}
            relatedProjects={relatedProjects}
            currentPo={project?.poNumber}
          />
        )}

        {safeActiveTab === 'team' && (
          <div className="max-w-xl max-md:max-w-full">
            <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6">
              <h3 className="text-indigo-400 text-[10px] font-black uppercase mb-4">Project Manager</h3>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-indigo-900/60 flex items-center justify-center text-indigo-200 font-bold">
                  {(project.projectManager?.name || 'Super Admin').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-semibold">{project.projectManager?.name || 'Super Admin'}</p>
                  <p className="text-zinc-300 text-xs">
                    {project.projectManager?.role === 'superadmin' ? 'Super Admin'
                      : project.projectManager?.role === 'projectManager' ? 'Project Manager'
                      : project.projectManager?.role === 'admin' ? 'Admin'
                      : 'Super Admin'}
                    {project.projectManager?.email ? ` · ${project.projectManager.email}` : ''}
                  </p>
                </div>
              </div>
              {isOwner ? (
                <div>
                  <label className="block text-zinc-300 text-xs mb-2">Reassign project manager</label>
                  <div className="flex items-center gap-3">
                    <select
                      value={project.projectManager?._id || ''}
                      onChange={(e) => handleSavePM(e.target.value)}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white"
                    >
                      {teamUsers.map(u => (
                        <option key={u._id} value={u._id}>
                          {u.name}{u.role === 'superadmin' ? ' (Super Admin)' : u.role === 'projectManager' ? ' (PM)' : u.role === 'admin' ? ' (Admin)' : ''}
                        </option>
                      ))}
                    </select>
                    {pmSaving && <span className="text-xs text-zinc-500">saving…</span>}
                  </div>
                </div>
              ) : (
                <p className="text-zinc-400 text-xs">Only Super Admin can reassign the project manager.</p>
              )}
            </div>

            {/* Additional (co-)project managers */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-indigo-400 text-[10px] font-black uppercase">Additional Project Managers</h3>
                {canManageCoPMs && (
                  <button
                    onClick={() => setAddingCoPM(v => !v)}
                    title="Add a co-project manager"
                    className="w-8 h-8 rounded-full bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center text-white transition-colors"
                  >
                    {addingCoPM ? <X size={16} /> : <Plus size={16} />}
                  </button>
                )}
              </div>

              {addingCoPM && (
                <div className="mb-4">
                  <select
                    defaultValue=""
                    onChange={(e) => handleAddCoPM(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white"
                  >
                    <option value="" disabled>Select a user to add…</option>
                    {teamUsers
                      .filter(u => u._id !== primaryPmId)
                      .filter(u => !(project.additionalManagers || []).some(m => (m.user?._id || m.user) === u._id))
                      .map(u => (
                        <option key={u._id} value={u._id}>
                          {u.name}{u.role === 'superadmin' ? ' (Super Admin)' : u.role === 'projectManager' ? ' (PM)' : u.role === 'admin' ? ' (Admin)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {(project.additionalManagers || []).length === 0 ? (
                <p className="text-zinc-400 text-xs">No additional project managers on this project.</p>
              ) : (
                <div className="space-y-3">
                  {(project.additionalManagers || []).map(m => {
                    const u = m.user || {};
                    const uid = u._id || String(m.user || '');
                    return (
                      <div key={uid} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-zinc-300 font-bold text-sm">
                          {(u.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{u.name || 'Unknown user'}</p>
                          <p className="text-zinc-300 text-xs truncate">
                            {u.role === 'superadmin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Project Manager'}
                            {u.email ? ` · ${u.email}` : ''}
                          </p>
                        </div>
                        {canManageCoPMs ? (
                          <>
                            <button
                              onClick={() => handleToggleCoPmNotif(uid)}
                              title={m.receiveNotifications ? 'Receives receipt notifications & nudges (click to turn off)' : 'Notifications off (click to turn on)'}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                m.receiveNotifications ? 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-800/60' : 'bg-gray-800 text-zinc-600 hover:bg-gray-700'
                              }`}
                            >
                              {m.receiveNotifications ? <Bell size={15} /> : <BellOff size={15} />}
                            </button>
                            <button
                              onClick={() => handleRemoveCoPM(uid)}
                              title="Remove co-project manager"
                              className="w-8 h-8 rounded-full bg-gray-800 text-zinc-500 hover:bg-red-900/40 hover:text-red-400 flex items-center justify-center transition-colors"
                            >
                              <X size={15} />
                            </button>
                          </>
                        ) : (
                          <span className={`text-[10px] uppercase font-bold ${m.receiveNotifications ? 'text-indigo-400' : 'text-zinc-600'}`}>
                            {m.receiveNotifications ? 'Notified' : 'Muted'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {coPmSaving && <p className="text-xs text-zinc-500 mt-3">saving…</p>}
              {canManageCoPMs && (
                <p className="text-zinc-400 text-[11px] mt-4">
                  The bell controls whether a co-manager also receives this project's receipt notifications and admin nudges. The primary PM always receives them.
                </p>
              )}
            </div>

            {/* Assigned Employees — the crew. Eyeball = can view the project;
                the three chips gate which tabs they see inside it. */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-indigo-400 text-[10px] font-black uppercase">Assigned Employees</h3>
                {canManageCoPMs && (
                  <button
                    onClick={() => setAddingEmp(v => !v)}
                    title="Assign an employee"
                    className="w-8 h-8 rounded-full bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center text-white transition-colors"
                  >
                    {addingEmp ? <X size={16} /> : <Plus size={16} />}
                  </button>
                )}
              </div>

              {addingEmp && (
                <div className="mb-4">
                  <select
                    defaultValue=""
                    onChange={(e) => handleAddEmployee(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white"
                  >
                    <option value="" disabled>Select an employee to assign…</option>
                    {employeeUsers
                      .filter(u => !(project.assignedEmployees || []).some(a => (a.user?._id || a.user) === u._id))
                      .map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                  {employeeUsers.length === 0 && (
                    <p className="text-zinc-400 text-xs mt-2">No employee-role user accounts yet — create them under Settings › Users.</p>
                  )}
                </div>
              )}

              {(project.assignedEmployees || []).length === 0 ? (
                <p className="text-zinc-400 text-xs">No employees assigned to this project.</p>
              ) : (
                <div className="space-y-3">
                  {(project.assignedEmployees || []).map(a => {
                    const u = a.user || {};
                    const uid = u._id || String(a.user || '');
                    const chips = [
                      ['timeline', 'Timeline'],
                      ['docsMedia', 'Docs & Media'],
                      ['receipts', 'Receipts'],
                    ];
                    return (
                      <div key={uid} className="py-2 border-b border-gray-800/40 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-zinc-300 font-bold text-sm shrink-0">
                            {(u.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-semibold truncate">{u.name || 'Unknown user'}</p>
                            <p className="text-zinc-300 text-xs truncate">{a.canView ? 'Can view this project' : 'Assigned — no access yet'}</p>
                          </div>
                          {canManageCoPMs ? (
                            <>
                              {/* the eyeball */}
                              <button
                                onClick={() => handleToggleEmpFlag(uid, 'canView')}
                                title={a.canView ? 'Can view this project (click to hide it from them)' : 'Cannot view this project (click to allow)'}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  a.canView ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800/50' : 'bg-gray-800 text-zinc-600 hover:bg-gray-700'
                                }`}
                              >
                                {a.canView ? <Eye size={15} /> : <EyeOff size={15} />}
                              </button>
                              <button
                                onClick={() => handleRemoveEmployee(uid)}
                                title="Remove from this project"
                                className="w-8 h-8 rounded-full bg-gray-800 text-zinc-500 hover:bg-red-900/40 hover:text-red-400 flex items-center justify-center transition-colors"
                              >
                                <X size={15} />
                              </button>
                            </>
                          ) : (
                            <span className={`text-[10px] uppercase font-bold ${a.canView ? 'text-emerald-400' : 'text-zinc-600'}`}>
                              {a.canView ? 'Visible' : 'Hidden'}
                            </span>
                          )}
                        </div>
                        {/* per-tab access chips */}
                        <div className="flex flex-wrap gap-1.5 mt-2 ml-12">
                          {chips.map(([flag, label]) => (
                            <button
                              key={flag}
                              disabled={!canManageCoPMs}
                              onClick={() => canManageCoPMs && handleToggleEmpFlag(uid, flag)}
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 transition-colors ${
                                a[flag]
                                  ? 'bg-indigo-600/20 text-indigo-300 ring-indigo-500/50'
                                  : 'bg-gray-800/80 text-zinc-400 ring-gray-600/70'
                              } ${canManageCoPMs ? 'cursor-pointer hover:ring-indigo-500/40' : 'cursor-default'}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {/* Docs & Media sub-permissions — segmented, visually
                            distinct from the chips. Default: Limited (own
                            uploads only). None hides the category entirely. */}
                        {a.docsMedia && (
                          <div className="ml-12 mt-2.5 bg-gray-950/50 border border-gray-800/60 rounded-xl p-2.5">
                            <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500 mb-1.5">Docs &amp; Media Permissions</p>
                            {[
                              ['docsView', 'Documents'],
                              ['videosView', 'Videos'],
                              ['galleryView', 'Gallery'],
                            ].map(([field, label]) => (
                              <div key={field} className="flex items-center justify-between gap-2 py-1">
                                <span className="text-[11px] text-zinc-300">{label}</span>
                                <div className="flex rounded-lg overflow-hidden border border-gray-700/70">
                                  {['all', 'limited', 'none'].map(v => {
                                    const on = (a[field] || 'limited') === v;
                                    return (
                                      <button
                                        key={v}
                                        disabled={!canManageCoPMs}
                                        onClick={() => canManageCoPMs && handleSetEmpPerm(uid, field, v)}
                                        className={`px-2 py-0.5 text-[9px] font-bold uppercase transition-colors ${
                                          on
                                            ? v === 'all' ? 'bg-emerald-600/40 text-emerald-200'
                                              : v === 'limited' ? 'bg-indigo-600/40 text-indigo-200'
                                              : 'bg-red-900/50 text-red-300'
                                            : 'bg-gray-900 text-zinc-600 hover:text-zinc-400'
                                        }`}
                                      >
                                        {v === 'all' ? 'Views All' : v === 'limited' ? 'Limited' : 'None'}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                            <p className="text-[9px] text-zinc-600 mt-1">Limited = only what they upload. None hides the category.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {empSaving && <p className="text-xs text-zinc-500 mt-3">saving…</p>}
              {canManageCoPMs && (
                <p className="text-zinc-400 text-[11px] mt-4">
                  Assigning alone grants nothing — open the eyeball for the project to appear in their portal, then flip on the tabs they may use.
                </p>
              )}
            </div>

            {/* Team History — everyone who has EVER been on this project.
                Append-only; removing someone never erases this (or their uploads). */}
            {teamHist.total > 0 && (
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-indigo-400 text-[10px] font-black uppercase">
                    Team History <span className="text-zinc-500 normal-case font-medium">({teamHist.entries.length} of {teamHist.total})</span>
                  </h3>
                  {isOwner && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Clear ALL team history for this project? This cannot be undone.')) return;
                        try {
                          await axios.delete(`${API_BASE}/projects/${id}/team-history`, { headers: { Authorization: localStorage.getItem('token') } });
                          setTeamHist({ entries: [], total: 0 });
                        } catch (err) {
                          const code = err.response?.status;
                          alert(`Could not clear team history${code === 404 ? ' — your local backend is running old code (restart it)' : code ? ` (${err.response?.data?.error || code})` : ''}.`);
                        }
                      }}
                      className="text-[10px] font-bold uppercase text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" onScroll={onHistScroll}>
                  {teamHist.entries.map((h, i) => {
                    const kindLabel = h.kind === 'primaryPM' ? 'Primary PM' : h.kind === 'coManager' ? 'Co-PM' : 'Employee';
                    const FIELD_LABEL = {
                      canView: 'Project view', timeline: 'Timeline', docsMedia: 'Docs & Media',
                      receipts: 'Receipts', receiveNotifications: 'Nudge notifications',
                      docsView: 'Documents access', videosView: 'Videos access', galleryView: 'Gallery access',
                    };
                    const changed = h.action === 'changed';
                    const toLabel = h.to === true || h.to === 'true' ? 'On' : h.to === false || h.to === 'false' ? 'Off' : String(h.to);
                    return (
                      <div key={h._id || i} onContextMenu={histContext(h._id)} title={isOwner ? 'Right-click to delete this entry' : undefined}
                        className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-gray-800/40 last:border-0">
                        <div className="min-w-0">
                          <span className="text-zinc-200 font-semibold">{h.user?.name || 'Removed user'}</span>
                          <span className="text-zinc-300 text-xs"> · {kindLabel}</span>
                          {changed && (
                            <p className="text-[11px] text-zinc-400">
                              {FIELD_LABEL[h.field] || h.field} → <span className={toLabel === 'On' ? 'text-emerald-300 font-semibold' : 'text-zinc-300 font-semibold'}>{toLabel}</span>
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                            h.action === 'added' ? 'bg-emerald-500/15 text-emerald-300'
                            : h.action === 'removed' ? 'bg-red-500/15 text-red-300'
                            : 'bg-amber-500/15 text-amber-300'
                          }`}>
                            {h.action}
                          </span>
                          <p className="text-[10px] text-zinc-400 mt-0.5">
                            {h.at ? new Date(h.at).toLocaleDateString() : ''}{h.by?.name ? ` · by ${h.by.name}` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {histLoading && <p className="text-zinc-500 text-xs py-1.5">loading more…</p>}
                </div>
                <p className="text-zinc-400 text-[11px] mt-3">
                  Removing someone from the team never deletes their uploads — receipts and media belong to the project.
                  {teamHist.entries.length < teamHist.total ? ' Scroll the list for older entries.' : ''}
                </p>
              </div>
            )}

            {/* right-click menu — delete a team-history entry (super admin only) */}
            {histCtx && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setHistCtx(null)} onContextMenu={(e) => { e.preventDefault(); setHistCtx(null); }} />
                <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[11rem]"
                  style={{ left: Math.min(histCtx.x, (window.innerWidth || 800) - 190), top: Math.min(histCtx.y, (window.innerHeight || 600) - 60) }}>
                  <button onClick={deleteHistEntry} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
                    Delete history entry
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {safeActiveTab === 'finances' && isOwner && <ProjectFinances highlightExpenseId={highlightExpenseId} openExpenses={openFinancesExpenses} />}

        {safeActiveTab === 'receipts' && role === 'employee' && <EmployeeReceipts projectId={id} />}
      </div>

      {showCopied && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-sm px-6 py-3 rounded-2xl shadow-2xl z-[10000]">
          Copied to clipboard!
        </div>
      )}
    </div>
  );
}

export default ProjectProfile;